import type {
  TEntityMaskItem,
  IEntityDataItem,
  ISelectItem,
  IEntitySelectItem,
} from '../models'
import { isEntityDataItem } from '../models'

// FieldSet

export interface IFieldSet extends IEntityDataItem {
  descr: string
  type: ISelectItem
  keyIdentifierFieldName?: string
  numFields?: number
}

export interface IFieldSetOverview {
  fieldSet: IFieldSet
  fields: TFieldSetItem[]
}

export interface IFieldSetWithFieldsConvertedToEntityMaskItems {
  fieldSet: IFieldSet
  fields: TEntityMaskItem[]
}

// FieldSetItems

interface IFieldSetItem extends IEntityDataItem {
  fieldSet: ISelectItem
  name: string
  label: string
  cols: number
  seqNum: number
  showForRoutes?: string[]
  showForRoutesDescr?: string // This is calculated... first letter of each showForRoutes element
  action: TFieldSetFieldActions | null
  actionField: ISelectItem | null
  actionVerb: TFieldSetFieldActionVerb | null
  actionFieldValue: string | null
}

export const availableShowForRouteArray = [
  'Companies',
  'Opportunities',
  'Orders',
]
export type TShowForRoute = (typeof availableShowForRouteArray)[number]
export type TShowForRouteArray = TShowForRoute[]

export type TFieldSetFieldTypes =
  | 'textField'
  | 'numberTextField'
  | 'currencyTextField'
  | 'percentageTextField'
  | 'textArea'
  | 'select'
  | 'autocomplete'
  | 'checkbox'
  | 'dateField'
  | 'divider'
  | 'subtitle'

export type TFieldSetFieldValidation =
  | 'required'
  | 'minLength'
  | 'email'
  | 'ipAddress'
  | 'subnetMask'
  | 'url'
  | 'tableHeaderWidth'
  | 'labelForFieldName'

export type TFieldSetFieldValidations = TFieldSetFieldValidation[]
export type TFieldSetFieldActions = 'Render if' | 'Enable if'

export const FIELDSETFIELD_ACTION_VERBS = [
  'Is',
  'Is not',
  'Includes',
  'Excludes',
] as const

export type TFieldSetFieldActionVerb =
  (typeof FIELDSETFIELD_ACTION_VERBS)[number]

export type TFieldSetFieldTableHeaderAlignment = 'Left' | 'Center' | 'Right'

export interface IFieldSetFieldTableHeader {
  alignment: TFieldSetFieldTableHeaderAlignment
  width: string // This should probably be number.  JR.
}

export interface IFieldSetField extends IFieldSetItem {
  type: TFieldSetFieldTypes
  selectFrom?: ISelectItem
  itemsString?: string
  doAllowMultipleSelections?: boolean
  tiedToPriorFieldCheckbox?: 'true'
  tableHeader?: IFieldSetFieldTableHeader
  tblAlignmentAndWidthDescr?: string // calculated from table header in client
  validations?: TFieldSetFieldValidations
  isKeyIdentifierField: boolean
}

export interface IFieldSetFieldAllPropertiesOptional
  extends Partial<IFieldSetField> {}

export interface IFieldSetSubtitle extends IFieldSetItem {
  type: 'subtitle'
}

export interface IFieldSetDivider extends IFieldSetItem {
  type: 'divider'
}

// Still Omit, so its fields are `any`: TOmitNamed exposes type errors in
// services/fieldSets.ts and the mock list sorts.
export interface IFieldSetSpacer extends Omit<IFieldSetItem, 'cols'> {
  type: 'spacer'
}

export type TFieldSetItem =
  | IFieldSetField
  | IFieldSetSubtitle
  | IFieldSetDivider
  | IFieldSetSpacer

export function isTFieldSetItem(
  fieldSetItem: unknown
): fieldSetItem is TFieldSetItem {
  return (
    (fieldSetItem as TFieldSetItem).hasOwnProperty('id') &&
    (fieldSetItem as TFieldSetItem).hasOwnProperty('fieldSet')
  )
}

export function isFieldSetField(
  fieldSetItem: TFieldSetItem
): fieldSetItem is IFieldSetField {
  // return (fieldSetItem as IFieldSetField).hasOwnProperty('canBeDeleted')
  return (fieldSetItem as IFieldSetField).hasOwnProperty('isKeyIdentifierField')
}

export function assertIsFieldSetField(
  fn: string,
  val: any
): asserts val is IFieldSetField {
  if (!isFieldSetField(val)) {
    throw new Error(`${fn} value should be a IFieldSetField, but it's not.`)
  }
}

export function assertIsFieldSetItemOrEntityDataItem(
  fn: string,
  val: unknown
): asserts val is TFieldSetItem | IEntityDataItem {
  if (!isTFieldSetItem(val) && !isEntityDataItem(val)) {
    throw new Error(
      `${fn} value should be a IFieldSetItem or an IEntityDataItem, but it's not.`
    )
  }
}

// parentObjectForCreate

export interface IFieldSetParentObjectForCreate {
  entityName: 'fieldSet'
  object: IEntitySelectItem
}
